import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { CheckboxModule } from 'primeng/checkbox';
import { ServiceOrderService } from '../../../services/service-order.service';
import { ServiceService } from '../../../services/service.service';
import { CustomerService } from '../../../services/customer.service';
import { Service } from '../../../models/service.model';
import { Customer } from '../../../models/customer.model';
import { ServiceOrderChecklistItem } from '../../../models/service-order-checklist.model';
import { CustomFieldDefinition, CustomFieldValue } from '../../../models/custom-field.model';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-form-service-order',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    DropdownModule,
    DatePickerModule,
    TextareaModule,
    TableModule,
    TabViewModule,
    CheckboxModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './form-service-order.component.html'
})
export class FormServiceOrderComponent implements OnInit {

  form!: FormGroup;
  isEditMode = false;
  id!: number;
  activeTabIndex = 0;
  companyId: number = 1; // TODO: Obter do contexto/auth
  
  services: Service[] = [];
  customers: Customer[] = [];
  
  // Checklist
  checklistItems: ServiceOrderChecklistItem[] = [];
  
  // Custom Fields
  customFieldDefinitions: CustomFieldDefinition[] = [];
  customFieldsMap: { [key: number]: any } = {};
  
  statusOptions = [
    { label: 'Pendente', value: 'PENDING' },
    { label: 'Concluída', value: 'COMPLETED' },
    { label: 'Cancelada', value: 'CANCELLED' }
  ];

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private serviceOrderService: ServiceOrderService,
    private serviceService: ServiceService,
    private customerService: CustomerService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadAuxiliaryData();
    
    const idParam = this.activatedRoute.snapshot.paramMap.get('id');
    console.log('ngOnInit - idParam:', idParam, 'companyId:', this.companyId);
    if (idParam) {
      this.id = parseInt(idParam);
      this.isEditMode = true;
      console.log('Edit mode - loading service order:', this.id);
      this.loadServiceOrder(this.id);
    } else {
      // Add one empty item by default
      console.log('Create mode - loading checklist template');
      this.addItem();
      this.loadChecklistTemplate();
    }
  }

  initForm() {
    this.form = this.fb.group({
      customerId: [null, Validators.required],
      date: [new Date(), Validators.required],
      status: ['PENDING', Validators.required],
      observation: [''],
      discount: [0],
      total: [0],
      items: this.fb.array([])
    });
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  loadAuxiliaryData() {
    // Load services - getting first 1000 active services
    this.serviceService.getAll('', true, 1).subscribe(res => {
      this.services = res.data; 
    });

    // Load customers - getting first 1000 active customers
    this.customerService.getAll('', true, 1).subscribe(res => {
      this.customers = res.data;
    });

    // Nota: customFieldDefinitions agora são carregados via getActiveTemplate()
    // quando estiver criando uma nova OS, ou via loadServiceOrder() quando editando
  }

  loadChecklistTemplate() {
    console.log('loadChecklistTemplate() called - companyId:', this.companyId);
    // Usa o novo endpoint /templates/active que retorna checklistItems e customFieldDefinitions
    console.log('Calling getActiveTemplate with companyId:', this.companyId);
    this.serviceOrderService.getActiveTemplate(this.companyId).subscribe({
      next: (response) => {
        console.log('getActiveTemplate response:', response);
        const templateData = response?.data;
        
        if (templateData) {
          console.log('Template data received:', templateData);
          // Carregar checklist items
          if (templateData.checklistItems && Array.isArray(templateData.checklistItems)) {
            this.checklistItems = templateData.checklistItems.map((item: any) => ({
              text: item.text,
              checked: false,
              order: item.order
            }));
            console.log('Checklist items loaded:', this.checklistItems.length);
          }
          
          // Carregar custom field definitions: backend pode retornar "customFieldDefinitions" ou "customFields"
          const rawFields = templateData.customFieldDefinitions ?? templateData.customFields;
          if (rawFields && Array.isArray(rawFields) && rawFields.length > 0) {
            this.customFieldDefinitions = rawFields
              .map((f: any) => ({
                id: f.id,
                name: f.name ?? '',
                label: f.label ?? f.name ?? '',
                fieldType: f.fieldType,
                required: f.required ?? false,
                options: f.options,
                order: f.order ?? 0,
                active: f.active,
                category: f.category
              }))
              .sort((a: CustomFieldDefinition, b: CustomFieldDefinition) => a.order - b.order);
            console.log('Custom field definitions loaded from active template:', this.customFieldDefinitions.length);
          }
        } else {
          console.warn('No template data in response');
        }
      },
      error: (err: any) => {
        console.error('Error loading active template', err);
        // Fallback: tentar carregar apenas o checklist template antigo se o novo endpoint falhar
        this.serviceOrderService.getChecklistTemplate(this.companyId).subscribe({
          next: (fallbackResponse) => {
            const template = fallbackResponse?.data;
            if (template && template.items && Array.isArray(template.items)) {
              this.checklistItems = template.items.map((item: any) => ({
                text: item.text,
                checked: false,
                order: item.order
              }));
            }
          },
          error: (fallbackErr: any) => {
            console.error('Error loading checklist template (fallback)', fallbackErr);
          }
        });
      }
    });
  }

  loadServiceOrder(id: number) {
    forkJoin({
      order: this.serviceOrderService.getById(id),
      checklist: this.serviceOrderService.getChecklistItems(id),
      customFields: this.serviceOrderService.getCustomFieldValues(id),
      fieldDefinitions: this.serviceOrderService.getCustomFieldDefinitions(undefined, this.companyId)
    }).subscribe({
      next: (results) => {
        // Load Order Data - new API format returns RestResponse with data
        const data = results.order?.data;

        if (data.date) data.date = new Date(data.date);
        
        this.form.patchValue({
          customerId: data.customerId,
          date: data.date,
          status: data.status,
          observation: data.observation,
          discount: data.discount,
          total: data.total
        });
        
        // Clear existing items
        while (this.items.length) {
          this.items.removeAt(0);
        }
        
        // Add items
        if (data.items) {
          data.items.forEach((item: any) => {
            this.addItem(item);
          });
        }
        
        this.calculateTotal();

        // Load Checklist Items - new API format returns RestResponse with data
        const checklistData = results.checklist?.data || [];
        if (checklistData && checklistData.length > 0) {
          this.checklistItems = checklistData.sort((a: any, b: any) => a.order - b.order);
        } else {
          // If no checklist items exist, load template
          this.loadChecklistTemplate();
        }

        // Load Custom Field Definitions first (from forkJoin result)
        const definitions = results.fieldDefinitions?.data || [];
        if (definitions.length > 0) {
          this.customFieldDefinitions = definitions.sort((a: CustomFieldDefinition, b: CustomFieldDefinition) => a.order - b.order);
        }

        // Load Custom Field Values - new API format returns RestResponse with data
        const customFieldsData = results.customFields?.data || [];
        console.log('Custom fields data loaded:', customFieldsData);
        console.log('Custom field definitions available:', this.customFieldDefinitions.length);
        
        if (customFieldsData && customFieldsData.length > 0) {
          customFieldsData.forEach((fieldValue: any) => {
            // Encontrar a definição do campo para converter o valor corretamente
            const fieldDef = this.customFieldDefinitions.find(def => def.id === fieldValue.customFieldDefinitionId);
            
            if (fieldDef) {
              // Converter valor baseado no tipo do campo
              let convertedValue: any = fieldValue.value;
              
              if (fieldValue.value != null && fieldValue.value !== '') {
                switch (fieldDef.fieldType) {
                  case 'DATE':
                    // Converter string para Date object
                    if (typeof fieldValue.value === 'string') {
                      const dateValue = new Date(fieldValue.value);
                      // Verificar se a data é válida
                      if (!isNaN(dateValue.getTime())) {
                        convertedValue = dateValue;
                      } else {
                        convertedValue = null;
                      }
                    } else if (fieldValue.value instanceof Date) {
                      convertedValue = fieldValue.value;
                    }
                    break;
                  case 'NUMBER':
                    // Converter string para number
                    if (typeof fieldValue.value === 'string') {
                      convertedValue = parseFloat(fieldValue.value);
                      if (isNaN(convertedValue)) {
                        convertedValue = null;
                      }
                    }
                    break;
                  case 'CHECKBOX':
                    // Converter string "true"/"false" para boolean
                    if (typeof fieldValue.value === 'string') {
                      convertedValue = fieldValue.value.toLowerCase() === 'true' || fieldValue.value === '1';
                    } else if (typeof fieldValue.value === 'number') {
                      convertedValue = fieldValue.value === 1;
                    }
                    break;
                  case 'TEXT':
                  case 'TEXTAREA':
                  case 'DROPDOWN':
                  default:
                    // Manter como string
                    convertedValue = String(fieldValue.value);
                    break;
                }
              } else {
                convertedValue = null;
              }
              
              this.customFieldsMap[fieldValue.customFieldDefinitionId] = convertedValue;
            } else {
              // Se não encontrar a definição, usar o valor como está (pode acontecer se a definição ainda não foi carregada)
              this.customFieldsMap[fieldValue.customFieldDefinitionId] = fieldValue.value;
            }
          });
        }
      },
      error: (err: any) => {
        console.error(err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar OS' });
      }
    });
  }

  createItem(data?: any): FormGroup {
    return this.fb.group({
      id: [data ? data.id : null],
      serviceId: [data ? data.serviceId : null, Validators.required],
      quantity: [data ? data.quantity : 1, [Validators.required, Validators.min(1)]],
      price: [data ? data.price : 0, [Validators.required, Validators.min(0)]],
      total: [data ? data.total : 0]
    });
  }

  addItem(data?: any) {
    const item = this.createItem(data);
    this.items.push(item);
    this.calculateTotal();
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.calculateTotal();
  }

  onServiceChange(index: number) {
    const item = this.items.at(index);
    const serviceId = item.get('serviceId')?.value;
    const service = this.services.find(s => s.id === serviceId);
    
    if (service) {
      item.patchValue({
        price: service.price
      });
      this.calculateItemTotal(index);
    }
  }

  calculateItemTotal(index: number) {
    const item = this.items.at(index);
    const qty = item.get('quantity')?.value || 0;
    const price = item.get('price')?.value || 0;
    const total = qty * price;
    
    item.patchValue({ total: total });
    this.calculateTotal();
  }

  calculateTotal() {
    let subtotal = 0;
    this.items.controls.forEach(control => {
      subtotal += control.get('total')?.value || 0;
    });
    
    const discount = this.form.get('discount')?.value || 0;
    const total = subtotal - discount;
    
    this.form.patchValue({ total: total < 0 ? 0 : total }, { emitEvent: false });
  }

  // Checklist Methods
  get checkedItemsCount(): number {
    return this.checklistItems.filter(item => item.checked).length;
  }

  addChecklistItem() {
    const newItem: ServiceOrderChecklistItem = {
      text: '',
      checked: false,
      order: this.checklistItems.length
    };
    this.checklistItems.push(newItem);
  }

  removeChecklistItem(index: number) {
    this.checklistItems.splice(index, 1);
    // Reorder
    this.checklistItems.forEach((item, i) => {
      item.order = i;
    });
  }

  // Custom Fields Methods
  getDropdownOptions(field: CustomFieldDefinition): any[] {
    if (!field.options) return [];
    
    // Backend pode retornar options como string JSON ou array
    let optionsArray: string[] = [];
    
    if (typeof field.options === 'string') {
      try {
        optionsArray = JSON.parse(field.options);
      } catch (e) {
        console.error('Error parsing options for field:', field.name, e);
        return [];
      }
    } else if (Array.isArray(field.options)) {
      optionsArray = field.options;
    }
    
    return optionsArray.map(opt => ({ label: opt, value: opt }));
  }

  saveAdditionalData(savedOrderId: number) {
    // Save Checklist Items
    const checklistSave = this.serviceOrderService.saveChecklistItems(savedOrderId, this.checklistItems);
    
    // Save Custom Field Values
    // Backend espera array direto no body, não objeto com propriedade "values"
    // Valores devem ser convertidos para string (backend espera System.String)
    // Filtrar campos vazios (value != null && value !== '')
    const customFieldValues: Array<{ customFieldDefinitionId: number; value: string }> = [];
    
    this.customFieldDefinitions.forEach(fieldDef => {
      const fieldValue = this.customFieldsMap[fieldDef.id];
      
      // Filtrar campos vazios
      if (fieldValue == null || fieldValue === '') {
        return;
      }
      
      // Converter valor para string sempre
      let stringValue: string;
      
      if (typeof fieldValue === 'boolean') {
        stringValue = fieldValue ? 'true' : 'false';
      } else if (fieldValue instanceof Date) {
        stringValue = fieldValue.toISOString().split('T')[0]; // Formato YYYY-MM-DD
      } else if (typeof fieldValue === 'number') {
        stringValue = fieldValue.toString();
      } else {
        stringValue = String(fieldValue);
      }
      
      customFieldValues.push({
        customFieldDefinitionId: fieldDef.id,
        value: stringValue
      });
    });
    
    console.log('Enviando custom field values (array direto):', customFieldValues);
    const customFieldsSave = this.serviceOrderService.saveCustomFieldValues(savedOrderId, customFieldValues);

    // Wait for both saves to complete
    forkJoin({
      checklist: checklistSave,
      customFields: customFieldsSave
    }).subscribe({
      next: (results) => {
        const checklistSuccess = results.checklist?.success !== false;
        const customFieldsSuccess = results.customFields?.success !== false;
        
        if (checklistSuccess && customFieldsSuccess) {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Sucesso', 
            detail: `OS ${this.isEditMode ? 'atualizada' : 'criada'} com sucesso` 
          });
          setTimeout(() => this.goBack(), 1000);
        } else {
          this.messageService.add({ 
            severity: 'warn', 
            summary: 'Atenção', 
            detail: 'OS salva, mas houve erro ao salvar informações adicionais' 
          });
          setTimeout(() => this.goBack(), 2000);
        }
      },
      error: (err: any) => {
        console.error('Error saving additional data', err);
        this.messageService.add({ 
          severity: 'warn', 
          summary: 'Atenção', 
          detail: 'OS salva, mas houve erro ao salvar informações adicionais' 
        });
        setTimeout(() => this.goBack(), 2000);
      }
    });
  }

  goBack() {
    this.router.navigate(['/app/service-orders']);
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha todos os campos obrigatórios' });
      return;
    }

    const formValue = this.form.value;
    
    const payload = {
      ...formValue,
      id: this.id
    };

    if (this.isEditMode) {
      // Update mode
      this.serviceOrderService.update(this.id, payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.saveAdditionalData(this.id);
          } else {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Erro', 
              detail: response.message || 'Erro ao atualizar OS' 
            });
          }
        },
        error: (err: any) => {
          console.error(err);
          const errorMessage = err?.error?.message || 'Erro ao atualizar OS';
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Erro', 
            detail: errorMessage 
            });
          }
        });
    } else {
      // Create mode
      this.serviceOrderService.create(payload).subscribe({
        next: (response) => {
          if (response.success) {
            // Get ID from response data (backend should return the created OS with ID)
            const savedOrderId = response.data?.id;
            if (savedOrderId) {
              this.saveAdditionalData(savedOrderId);
            } else {
            this.messageService.add({ 
                severity: 'error', 
                summary: 'Erro', 
                detail: 'OS criada mas ID não foi retornado. Contate o suporte.' 
              });
            }
          } else {
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Erro', 
              detail: response.message || 'Erro ao criar OS' 
            });
          }
      },
        error: (err: any) => {
        console.error(err);
          const errorMessage = err?.error?.message || 'Erro ao criar OS';
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
            detail: errorMessage 
        });
      }
    });
    }
  }
}
